/* product.js — CRO product page JS
 * Handles: variant selector, gallery thumbnails, quick-view modal.
 * Pincode, qty steppers, wishlist, and FAQ are handled by theme.js.
 * Add-to-cart and buy-now are handled by cart.js via delegated events.
 *
 * Data-attribute contracts:
 *   [data-product-json]         — <script> element with variants JSON
 *   [data-product-form]         — <form> wrapping the buy panel
 *   [data-product-handle]       — attribute on form (or section wrapper)
 *   [data-variant-id]           — hidden <input name="id"> inside the form
 *   [data-price-wrapper]        — container updated on variant change
 *   [data-mobile-price]         — <small> in mobile sticky bar
 *   [data-money-format]         — attribute on section wrapper with Shopify money format
 *   [data-option-selector]      — <fieldset> per option
 *   [data-option-value]         — <button> within each fieldset
 *   [data-option-index]         — index (0-based) on each option value button
 *   [data-selected-option]      — <span> showing selected value in legend
 *   [data-product-media]        — media slide div; value = media.id
 *   [data-thumb]                — thumbnail button
 *   [data-media-id]             — media id on thumb button
 *   [data-low-stock]            — low-stock indicator div (updated on variant change)
 *   [data-add-to-cart]          — handled by cart.js
 *   [data-buy-now]              — handled by cart.js
 *   [data-quick-view-modal]     — modal shell (snippet: quick-view.liquid)
 *   [data-quick-view-close]     — close button inside modal
 *   [data-qv-type], [data-qv-title], [data-qv-price],
 *   [data-qv-description], [data-qv-options], [data-qv-link] — modal slots
 *   [data-quick-view="HANDLE"]  — trigger on product cards (delegated)
 *   [data-mobile-cart]          — mobile sticky buy bar (aria-hidden toggled)
 */
(function () {
  'use strict';

  /* ─────────────────────────────────────────────
   * Money formatting
   * Reads the Shopify money_format string from [data-money-format]
   * on the section wrapper and substitutes the price.
   * Prices in Shopify variants are in paise (integer, e.g. 59900 = ₹599.00).
   * ───────────────────────────────────────────── */
  var _moneyFormat = null;

  function getMoneyFormat() {
    if (_moneyFormat) return _moneyFormat;
    var el = document.querySelector('[data-money-format]');
    _moneyFormat = el ? el.dataset.moneyFormat : '{{amount}}';
    return _moneyFormat;
  }

  function formatMoney(cents) {
    var fmt = getMoneyFormat();

    /* Helper: format paise into a display string.
     * @param {number} paise        — integer (e.g. 159900)
     * @param {string} thousandsSep — separator between digit groups (',' or '.')
     * @param {string} decimalSep   — separator before decimal part ('.' or ',')
     * @param {number} decimals     — 0 or 2
     */
    function formatAmount(paise, thousandsSep, decimalSep, decimals) {
      var total = paise / 100;
      var intPart = Math.floor(total).toString();
      /* Apply 3-digit thousands grouping to the integer part */
      intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSep);
      if (decimals === 0) return intPart;
      var decPart = (Math.round((total - Math.floor(total)) * 100)).toString();
      if (decPart.length < 2) decPart = '0' + decPart;
      return intPart + decimalSep + decPart;
    }

    /* Map each Shopify money token to the correct formatted string */
    return fmt
      .replace(/\{\{\s*amount\s*\}\}/g,
        formatAmount(cents, ',', '.', 2))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g,
        formatAmount(cents, ',', '.', 0))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g,
        formatAmount(cents, '.', ',', 2))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g,
        formatAmount(cents, '.', ',', 0));
  }

  /* ─────────────────────────────────────────────
   * Variant selector
   * ───────────────────────────────────────────── */
  function initVariantSelector(root) {
    var jsonScript = root.querySelector('[data-product-json]');
    var form = root.querySelector('[data-product-form]') || document.querySelector('[data-product-form]');
    if (!jsonScript || !form) return;
    if (form.dataset.variantInit) return;
    form.dataset.variantInit = '1';

    var variants;
    try {
      variants = JSON.parse(jsonScript.textContent);
    } catch (e) {
      console.error('[product.js] Could not parse product variants JSON:', e);
      return;
    }

    var variantIdInput = form.querySelector('[data-variant-id]');
    var priceWrapper = root.querySelector('[data-price-wrapper]') || document.querySelector('[data-price-wrapper]');
    var mobilePrice = root.querySelector('[data-mobile-price]') || document.querySelector('[data-mobile-price]');
    var atcBtn = form.querySelector('[data-add-to-cart]');
    var mobileAtcBtn = (root.querySelector('[data-mobile-cart]') || document.querySelector('[data-mobile-cart]'))
      ? (root.querySelector('[data-mobile-cart] [data-add-to-cart]') || document.querySelector('[data-mobile-cart] [data-add-to-cart]'))
      : null;
    var buyNowBtn = form.querySelector('[data-buy-now]');

    /* Build current selections from active buttons */
    function getSelectedOptions() {
      var options = [];
      form.querySelectorAll('[data-option-selector]').forEach(function (fieldset) {
        var active = fieldset.querySelector('[data-option-value].is-active');
        options.push(active ? active.dataset.optionValue : '');
      });
      return options;
    }

    /* Find matching variant */
    function findVariant(options) {
      if(!options.length && variants.length===1)return variants[0];
      return variants.find(function (v) {
        return v.options.every(function (opt, i) {
          return opt === options[i];
        });
      }) || null;
    }

    /* Switch main gallery image to the variant's featured media */
    function switchMedia(mediaId) {
      if (!mediaId) return;
      var strId = String(mediaId);
      var slides = root.querySelectorAll('[data-product-media]');
      var thumbs = root.querySelectorAll('[data-thumb]');

      slides.forEach(function (slide) {
        var isTarget = slide.dataset.productMedia === strId;
        slide.style.display = isTarget ? '' : 'none';
        slide.setAttribute('aria-hidden', isTarget ? 'false' : 'true');
      });

      thumbs.forEach(function (thumb) {
        var isTarget = thumb.dataset.mediaId === strId;
        thumb.classList.toggle('is-active', isTarget);
        thumb.setAttribute('aria-pressed', isTarget ? 'true' : 'false');
      });
    }

    /* Update price display */
    function renderPrice(variant) {
      if (!priceWrapper) return;
      if (!variant) {
        priceWrapper.innerHTML = '';
        return;
      }

      var priceHtml = '<p class="price">';
      priceHtml += formatMoney(variant.price);

      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        var savePct = Math.round((variant.compare_at_price - variant.price) / variant.compare_at_price * 100);
        priceHtml += ' <s>' + formatMoney(variant.compare_at_price) + '</s>';
        priceHtml += ' <span class="badge badge--sale">Save ' + savePct + '%</span>';
      }
      priceHtml += '</p>';
      priceWrapper.innerHTML = priceHtml;

      if (mobilePrice) {
        mobilePrice.textContent = formatMoney(variant.price);
      }
    }

    /* Update ATC / Buy Now availability */
    function renderAvailability(variant) {
      var available = variant && variant.available;

      /* Update both the main ATC and the mobile-sticky ATC uniformly:
         both should be disabled + labelled "Sold Out" when unavailable.
         The previous guard (!btn.closest('[data-mobile-cart]') === false) was
         a precedence bug — the guard is removed so both buttons are updated. */
      [atcBtn, mobileAtcBtn].forEach(function (btn) {
        if (!btn) return;
        btn.disabled = !available;
        if (!btn.dataset.buyNow) {
          btn.textContent = available ? 'Add to bag' : 'Sold out';
        }
      });

      if (buyNowBtn) {
        buyNowBtn.disabled = !available;
      }

      /* Update data-variant-id on buy-now buttons */
      if (variant) {
        if (buyNowBtn) buyNowBtn.dataset.variantId = variant.id;
        if (mobileAtcBtn) mobileAtcBtn.dataset.variantId = variant.id;
      }
    }

    /* Update hidden variant id input */
    function setVariantId(variant) {
      if (!variantIdInput) return;
      variantIdInput.value = variant ? variant.id : '';
      variantIdInput.disabled = !variant;
    }

    /* Update URL with ?variant= */
    function updateUrl(variant) {
      if (!variant || !history.replaceState) return;
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState(null, '', url.toString());
    }

    /* Update low-stock indicator */
    function renderLowStock(variant) {
      var el = document.querySelector('[data-low-stock]');
      if (!el) return;
      var threshold = 8;
      var show = variant &&
        variant.inventory_management &&
        variant.inventory_quantity > 0 &&
        variant.inventory_quantity < threshold;

      if (show) {
        var qty = variant.inventory_quantity;
        var width = Math.max(qty * 10, 24);
        el.style.display = '';
        var countEl = el.querySelector('[data-low-stock-count]');
        if (countEl) countEl.textContent = 'Only ' + qty + ' left in stock';
        var bar = el.querySelector('b');
        if (bar) bar.style.width = width + '%';
      } else {
        el.style.display = 'none';
      }
    }

    /* Main update function */
    function onOptionChange() {
      var options = getSelectedOptions();
      var variant = findVariant(options);

      setVariantId(variant);
      renderPrice(variant);
      renderAvailability(variant);
      renderLowStock(variant);
      updateUrl(variant);

      if (variant && variant.featured_media) {
        switchMedia(variant.featured_media.id);
      }
    }

    /* Delegate clicks on option value buttons */
    form.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-option-value]');
      if (!btn) return;

      var fieldset = btn.closest('[data-option-selector]');
      if (!fieldset) return;

      /* Toggle active state within this fieldset */
      fieldset.querySelectorAll('[data-option-value]').forEach(function (b) {
        b.classList.remove('is-active');
        b.setAttribute('aria-pressed', 'false');
      });
      btn.classList.add('is-active');
      btn.setAttribute('aria-pressed', 'true');

      /* Update legend selected display */
      var selectedSpan = fieldset.querySelector('[data-selected-option]');
      if (selectedSpan) selectedSpan.textContent = btn.dataset.optionValue;

      onOptionChange();
    });

    /* Honour ?variant= param on page load */
    (function applyUrlVariant() {
      var params = new URLSearchParams(window.location.search);
      var variantId = params.get('variant');
      if (!variantId) {
        onOptionChange();
        return;
      }
      var target = variants.find(function (v) { return String(v.id) === variantId; });
      if (!target) {
        onOptionChange();
        return;
      }

      /* Set each option button to match target variant */
      form.querySelectorAll('[data-option-selector]').forEach(function (fieldset, idx) {
        var optionValue = target.options[idx];
        if (!optionValue) return;

        fieldset.querySelectorAll('[data-option-value]').forEach(function (btn) {
          var match = btn.dataset.optionValue === optionValue;
          btn.classList.toggle('is-active', match);
          btn.setAttribute('aria-pressed', match ? 'true' : 'false');
        });

        var selectedSpan = fieldset.querySelector('[data-selected-option]');
        if (selectedSpan) selectedSpan.textContent = optionValue;
      });

      onOptionChange();
    })();
  }

  /* ─────────────────────────────────────────────
   * Gallery thumbnail clicks
   * ───────────────────────────────────────────── */
  function initGallery(root) {
    var thumbContainer = root.querySelector('.reference-gallery__thumbs') ||
      document.querySelector('.reference-gallery__thumbs');
    if (!thumbContainer) return;
    if (thumbContainer.dataset.galleryInit) return;
    thumbContainer.dataset.galleryInit = '1';

    thumbContainer.addEventListener('click', function (e) {
      var thumb = e.target.closest('[data-thumb]');
      if (!thumb) return;

      /* Toggle active thumb */
      thumbContainer.querySelectorAll('[data-thumb]').forEach(function (t) {
        t.classList.remove('is-active');
        t.setAttribute('aria-pressed', 'false');
      });
      thumb.classList.add('is-active');
      thumb.setAttribute('aria-pressed', 'true');

      /* Show corresponding slide */
      var mediaId = thumb.dataset.mediaId;
      if (!mediaId) return;

      document.querySelectorAll('[data-product-media]').forEach(function (slide) {
        var isTarget = slide.dataset.productMedia === mediaId;
        slide.style.display = isTarget ? '' : 'none';
        slide.setAttribute('aria-hidden', isTarget ? 'false' : 'true');
      });
    });

    /* Keyboard support on thumb buttons */
    thumbContainer.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      var thumb = e.target.closest('[data-thumb]');
      if (!thumb) return;
      e.preventDefault();
      thumb.click();
    });
  }

  /* ─────────────────────────────────────────────
   * Quick-view modal
   * Trigger:  [data-quick-view="HANDLE"] on product cards
   * Shell:    [data-quick-view-modal] rendered by quick-view.liquid
   * Slots:    [data-qv-type], [data-qv-title], [data-qv-price],
   *           [data-qv-description], [data-qv-options], [data-qv-link]
   * Add-to-cart in QV: dispatches 'cart:added' then cart.js handles drawer open.
   * ───────────────────────────────────────────── */
  var _qvOpen = false;
  var _qvRequest=0;
  var _qvController=null;
  var _qvLastOpener = null;
  var _qvEscHandler = null;

  function openQuickView() {
    var modal = document.querySelector('[data-quick-view-modal]');
    if (!modal) return;
    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    if(window.AinazOverlay)window.AinazOverlay.open(modal,closeQuickView,_qvLastOpener);
    _qvOpen = true;

    var closeBtn = modal.querySelector('[data-quick-view-close]');
    if (closeBtn) closeBtn.focus();

    _qvEscHandler = function (e) {
      if (e.key === 'Escape') closeQuickView();
    };
    document.addEventListener('keydown', _qvEscHandler);
  }

  function closeQuickView() {
    _qvRequest++;
    if(_qvController)_qvController.abort();
    var modal = document.querySelector('[data-quick-view-modal]');
    if (!modal) return;
    modal.hidden = true;
    modal.setAttribute('aria-hidden', 'true');
    if(_qvOpen && window.AinazOverlay)window.AinazOverlay.close();
    _qvOpen = false;

    if (_qvEscHandler) {
      document.removeEventListener('keydown', _qvEscHandler);
      _qvEscHandler = null;
    }
    if (_qvLastOpener) {
      _qvLastOpener.focus();
      _qvLastOpener = null;
    }
  }

  function populateQuickView(data, features) {
    var modal = document.querySelector('[data-quick-view-modal]');
    if (!modal) return;

    /* Remove any previously injected image */
    var existingImg = modal.querySelector('.quick-modal > img');
    if (existingImg) existingImg.remove();modal.querySelector('.qv-thumbnails')?.remove();

    /* Inject image */
    if (data.featured_image) {
      var img = document.createElement('img');
      img.src = data.featured_image;
      img.alt = data.title || '';
      img.loading = 'lazy';
      var quickModal = modal.querySelector('.quick-modal');
      var content = quickModal && quickModal.querySelector('.quick-modal__content');
      if (quickModal && content) {
        quickModal.insertBefore(img, content);img.dataset.qvImage='';
        var thumbs=document.createElement('div');thumbs.className='qv-thumbnails';(data.images||[data.featured_image]).forEach(function(url,i){var thumb=document.createElement('button');thumb.type='button';thumb.setAttribute('aria-label','View image '+(i+1));thumb.setAttribute('aria-pressed',String(i===0));var photo=document.createElement('img');photo.src=typeof url==='string'?url:url.src;photo.alt='';thumb.append(photo);thumb.onclick=function(){img.src=photo.src;Array.from(thumbs.children).forEach(function(b){b.setAttribute('aria-pressed',String(b===thumb));});};thumbs.append(thumb);});quickModal.insertBefore(thumbs,content);

      }
    }

    /* Category / type */
    var typeEl = modal.querySelector('[data-qv-type]');
    if (typeEl) typeEl.textContent = data.type || '';

    /* Title */
    var titleEl = modal.querySelector('[data-qv-title]');
    if (titleEl) titleEl.textContent = data.title || '';

    /* Price */
    var priceEl = modal.querySelector('[data-qv-price]');
    if (priceEl && data.price) {
      priceEl.textContent = formatMoney(data.price);
    }

    /* Description (first paragraph or snippet) */
    var descEl = modal.querySelector('[data-qv-description]');
    if (descEl) {
      var desc = data.description || '';
      /* Strip HTML tags for a plain-text snippet */
      var tmp = document.createElement('div');
      tmp.innerHTML = desc;
      descEl.textContent = (tmp.textContent || '').slice(0, 180);
    }

    var optEl=modal.querySelector('[data-qv-options]');
    var linkEl=modal.querySelector('[data-qv-link]');
    if(linkEl){linkEl.href='/products/'+data.handle;linkEl.style.display='';}
    var previous=modal.querySelector('[data-qv-purchase]');if(previous)previous.remove();
    optEl.innerHTML='';
    var purchase=document.createElement('div');purchase.dataset.qvPurchase='';
    var select=document.createElement('select');select.setAttribute('aria-label','Choose product option');
    data.variants.forEach(function(variant){var option=document.createElement('option');option.value=variant.id;option.textContent=variant.title+' — '+formatMoney(variant.price)+(variant.available?'':' — Sold out');option.disabled=!variant.available;select.appendChild(option);});
    var first=data.variants.find(function(variant){return variant.available;});if(first)select.value=first.id;
    var quantity=document.createElement('input');quantity.type='number';quantity.min='1';quantity.step='1';quantity.value='1';quantity.setAttribute('aria-label','Quantity');
    var gift=document.createElement('input');gift.type='text';gift.placeholder='Gift message (optional)';gift.setAttribute('aria-label','Gift message');gift.maxLength=250;
    var button=document.createElement('button');button.className='button button--primary';button.textContent=first?'Add to bag':'Sold out';button.disabled=!first;
    var feedback=document.createElement('p');feedback.setAttribute('role','status');
    select.onchange=function(){var variant=data.variants.find(function(v){return String(v.id)===select.value;});button.disabled=!variant||!variant.available;if(priceEl&&variant)priceEl.textContent=formatMoney(variant.price);};
    var purchasing=false;function purchaseSelection(checkout){if(purchasing)return;var units=Number(quantity.value);if(!Number.isInteger(units)||units<1){feedback.textContent='Enter a quantity of at least one.';return;}purchasing=true;button.disabled=true;if(buyNow)buyNow.disabled=true;var properties={};if(gift.value.trim())properties['Gift message']=gift.value.trim();var purchaseOpener=_qvLastOpener;window.AinazCart.add([{id:Number(select.value),quantity:units,properties:properties}],{checkout:Boolean(checkout),trigger:purchaseOpener,onAccepted:closeQuickView}).then(function(){}).catch(function(error){feedback.textContent=error.message;}).finally(function(){purchasing=false;var available=data.variants.some(function(v){return String(v.id)===select.value&&v.available;});button.disabled=!available;if(buyNow)buyNow.disabled=!available;});}
    button.onclick=function(){purchaseSelection(false);};
    select.className='qv-native-select';
    var variants=document.createElement('fieldset');variants.className='variant-picker qv-variants';var legend=document.createElement('legend');legend.textContent='Choose your option';variants.append(legend,select);
    if(data.variants.length>1){var chips=document.createElement('div');chips.className='variant-chips';data.variants.forEach(function(variant){var chip=document.createElement('button');chip.type='button';chip.textContent=variant.title;chip.disabled=!variant.available;chip.setAttribute('aria-pressed',String(first&&variant.id===first.id));chip.onclick=function(){select.value=String(variant.id);select.onchange();Array.from(chips.children).forEach(function(other){other.setAttribute('aria-pressed',String(other===chip));});};chips.append(chip);});variants.append(chips);select.classList.add('visually-hidden');}else variants.hidden=true;
    var quantityLabel=document.createElement('label');quantityLabel.textContent='Quantity';quantityLabel.className='qv-quantity-label';var stepper=document.createElement('div');stepper.className='quantity-stepper';[-1,1].forEach(function(delta){var control=document.createElement('button');control.type='button';control.setAttribute('aria-label',delta<0?'Decrease quantity':'Increase quantity');var glyph=document.querySelector('template[data-parity-icon="'+(delta<0?'minus':'plus')+'"]');if(glyph)control.append(glyph.content.cloneNode(true));control.onclick=function(){quantity.value=String(Math.max(1,Math.min(99,Number(quantity.value)+delta)));};stepper.append(control);if(delta<0)stepper.append(quantity);});quantityLabel.append(stepper);
    var giftDetails=document.createElement('details');giftDetails.className='qv-gift';var giftSummary=document.createElement('summary');giftSummary.textContent='Add a gift message';giftDetails.append(giftSummary,gift);
    purchase.append(variants,quantityLabel,giftDetails);if(features)purchase.append(document.importNode(features,true));purchase.append(button);var buyNow=document.createElement('button');buyNow.type='button';buyNow.className='button secondary';buyNow.textContent='Buy now';buyNow.disabled=!first;buyNow.onclick=function(){purchaseSelection(true);};purchase.append(buyNow,feedback);var support=modal.querySelector('template[data-qv-support]');if(support){purchase.append(support.content.cloneNode(true));purchase.querySelector('[data-qv-details]').textContent=descEl.textContent;}optEl.after(purchase);window.AinazDisclosures?.init(purchase);
    modal.querySelector('.quick-modal').classList.add('is-populated');
  }

  function initQuickView(root) {
    /* Close button inside modal */
    document.querySelectorAll('[data-quick-view-close]').forEach(function (btn) {
      if (btn.dataset.qvInit) return;
      btn.dataset.qvInit = '1';
      btn.addEventListener('click', closeQuickView);
    });

    /* Backdrop click */
    var modal = document.querySelector('[data-quick-view-modal]');
    if (modal && !modal.dataset.backdropInit) {
      modal.dataset.backdropInit = '1';
      modal.addEventListener('click', function (e) {
        /* Close only when clicking the backdrop itself, not the inner modal */
        if (e.target === modal) closeQuickView();
      });
    }
  }

  /* Delegated quick-view trigger — document-level so product cards on PDP (related) work */
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-quick-view]');
    if (!trigger) return;
    e.preventDefault();

    var handle = trigger.dataset.quickView;
    if (!handle || !/^[a-z0-9-]+$/.test(handle)) return;

    if(_qvOpen)closeQuickView();
    _qvLastOpener = trigger;
    var requestId=++_qvRequest;
    if(_qvController)_qvController.abort();
    _qvController=new AbortController();

    /* Show loading state */
    var modal = document.querySelector('[data-quick-view-modal]');
    if (modal) {
      var titleEl = modal.querySelector('[data-qv-title]');
      if (titleEl) titleEl.textContent = 'Loading…';
      var previousPurchase=modal.querySelector('[data-qv-purchase]');if(previousPurchase)previousPurchase.remove();
      ['[data-qv-options]','[data-qv-price]','[data-qv-description]'].forEach(function(selector){var node=modal.querySelector(selector);if(node)node.textContent='';});
      var oldImage=modal.querySelector('.quick-modal > img');if(oldImage)oldImage.remove();
      var detailsLink=modal.querySelector('[data-qv-link]');if(detailsLink)detailsLink.style.display='none';
    }
    openQuickView();

    var productPath = ((window.Shopify && window.Shopify.routes.root) || '/') + 'products/' + handle;
    var previewId = new URLSearchParams(window.location.search).get('preview_theme_id');
    var htmlPath = productPath + (previewId ? '?preview_theme_id=' + encodeURIComponent(previewId) : '');
    var requestOptions = {signal:_qvController.signal,credentials:'same-origin'};
    var detailsRequest = fetch(htmlPath,requestOptions).then(function(response){
      if(!response.ok)return null;
      return response.text().then(function(html){
        return new DOMParser().parseFromString(html,'text/html').querySelector('.ainaz-main-product .product-features');
      });
    }).catch(function(){return null;});
    Promise.all([
      fetch(productPath + '.js',requestOptions).then(function(response){
        if(!response.ok)throw new Error('Product fetch failed: ' + response.status);
        return response.json();
      }),
      detailsRequest
    ])
      .then(function (results) {
        if(requestId!==_qvRequest||!_qvOpen)return;
        populateQuickView(results[0],results[1]);
      })
      .catch(function (err) {
        if(err.name==='AbortError'||requestId!==_qvRequest)return;
        var heading=modal.querySelector('[data-qv-title]');if(heading)heading.textContent='Unable to load this piece. Please try again.';
      });
  });

  /* ─────────────────────────────────────────────
   * Mobile sticky bar visibility
   * Show bar only on mobile (≤760px) per prototype class reference-sticky-buy
   * (theme.css:4985 shows it via display:grid at ≤760px).
   * We also update aria-hidden so it does not confuse screen readers on desktop.
   * ───────────────────────────────────────────── */
  function initMobileStickyBar() {
    var bar=document.querySelector('[data-mobile-cart]');
    var primary=document.querySelector('[data-product-form] [data-add-to-cart]');
    if(!bar||!primary||bar.dataset.stickyInit)return;
    bar.dataset.stickyInit='1';
    var observer=new IntersectionObserver(function(entries){var show=!entries[0].isIntersecting&&matchMedia('(max-width:760px)').matches;bar.style.visibility=show?'visible':'hidden';bar.setAttribute('aria-hidden',String(!show));});
    observer.observe(primary);
    document.addEventListener('shopify:section:unload',function unload(event){if(event.target.contains(primary)){observer.disconnect();document.removeEventListener('shopify:section:unload',unload);}});
  }

  /* ─────────────────────────────────────────────
   * initAll — run all modules
   * ───────────────────────────────────────────── */
  function initAll(root) {
    initVariantSelector(root);
    initGallery(root);
    initQuickView(root);
    initMobileStickyBar();
  }

  document.addEventListener('change',function(event){if(!event.target.matches('[data-gift-note-toggle]'))return;var field=document.getElementById(event.target.getAttribute('aria-controls'));if(field){field.style.display=event.target.checked?'block':'none';field.disabled=!event.target.checked;if(!event.target.checked)field.value='';}});

  /* ─────────────────────────────────────────────
   * Bootstrap
   * ───────────────────────────────────────────── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  /* Theme Editor re-init */
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });

})();
